import { useCallback, useEffect, useRef, useState } from 'react'
import { BrowserQRCodeReader, type IScannerControls } from '@zxing/browser'
import { useNavigate } from 'react-router-dom'
import { httpClientService, vesselStoreService } from '@services'
import { useLanguage } from '@i18n'
import type { Certificate, Vessel } from '@app-types/index'
import './QRCode.css'

const getQrId = (qrScanResult: string) => {
  const trimmedValue = qrScanResult.trim()

  try {
    const url = new URL(trimmedValue, window.location.origin)
    const queryId = url.searchParams.get('id') || url.searchParams.get('certificateID')

    if (queryId?.trim()) {
      return queryId.trim()
    }
  } catch {
    // Non-URL QR values are handled as raw certificate IDs below.
  }

  const cleanValue = trimmedValue.split('#')[0].split('?')[0].replace(/\/+$/, '')
  const lastSlashIndex = cleanValue.lastIndexOf('/')

  return lastSlashIndex >= 0 ? cleanValue.substring(lastSlashIndex + 1) : cleanValue
}

const getScannedCertificate = (vessel: Vessel, qrId: string): Certificate | null => {
  const certificates = vessel.certificates || []

  return (
    certificates.find((certificate) => certificate.uuid === qrId) ||
    certificates.find((certificate) => certificate.seqNo === qrId) ||
    (certificates.length === 1 ? certificates[0] : null)
  )
}

const setScannedUtn = (vessel: Vessel, certificate: Certificate, qrId: string): Vessel => ({
  ...vessel,
  certificates: vessel.certificates.map((item) =>
    item.uuid === certificate.uuid ? { ...item, seqNo: item.seqNo || qrId } : item
  ),
})

export const QRCode = () => {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const controlsRef = useRef<IScannerControls | null>(null)
  const processingScanRef = useRef(false)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [hasCamera, setHasCamera] = useState<boolean | null>(null)
  const [isStarting, setIsStarting] = useState(true)
  const [isResolving, setIsResolving] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const stopScanner = useCallback(() => {
    controlsRef.current?.stop()
    controlsRef.current = null
  }, [])

  const resolveQrCode = useCallback(
    async (qrScanResult: string, controls?: IScannerControls) => {
      if (processingScanRef.current) {
        return
      }

      const qrId = getQrId(qrScanResult)

      if (!qrId) {
        setErrorMessage(t('qrcode.noCertificateId'))
        return
      }

      processingScanRef.current = true
      if (controls) {
        controls.stop()
      } else {
        controlsRef.current?.stop()
      }
      controlsRef.current = null
      setErrorMessage('')
      setIsResolving(true)

      try {
        const response = await httpClientService.getCertificateStatus(qrId, false)
        const vessel = response.data

        if (!vessel?.vesselID) {
          setErrorMessage(t('qrcode.noCertificateFound'))
          return
        }

        const certificate = getScannedCertificate(vessel, qrId)

        if (!certificate) {
          setErrorMessage(t('qrcode.noCertificateFound'))
          return
        }

        const scannedVessel = setScannedUtn(vessel, certificate, qrId)
        vesselStoreService.setVessel(scannedVessel)
        navigate(`/vessels/vessel/${scannedVessel.vesselID}/${certificate.uuid || qrId}`)
      } catch {
        setErrorMessage(t('qrcode.somethingWentWrong'))
      } finally {
        setIsResolving(false)
        processingScanRef.current = false
      }
    },
    [navigate, t]
  )

  useEffect(() => {
    let cancelled = false
    const videoElement = videoRef.current

    if (!videoElement) {
      return undefined
    }

    const reader = new BrowserQRCodeReader(undefined, {
      delayBetweenScanAttempts: 300,
      delayBetweenScanSuccess: 500,
    })

    const startScanner = async () => {
      try {
        setIsStarting(true)
        setErrorMessage('')
        stopScanner()

        const controls = await reader.decodeFromConstraints(
          {
            video: {
              facingMode: { exact: 'environment' },
            },
          },
          videoElement,
          (result, error, scannerControls) => {
            if (result) {
              void resolveQrCode(result.getText(), scannerControls)
              return
            }

            void error
          }
        )

        if (cancelled) {
          controls.stop()
          return
        }

        controlsRef.current = controls
        setHasPermission(true)
        setHasCamera(true)
      } catch (error) {
        if (cancelled) {
          return
        }

        const name = error instanceof Error ? error.name : ''
        setHasPermission(name === 'NotAllowedError' || name === 'PermissionDeniedError' ? false : null)
        setHasCamera(false)
        setErrorMessage(
          name === 'NotAllowedError' || name === 'PermissionDeniedError'
            ? t('qrcode.cameraPermissionDenied')
            : t('qrcode.cameraStartFailed')
        )
      } finally {
        if (!cancelled) {
          setIsStarting(false)
        }
      }
    }

    void startScanner()

    return () => {
      cancelled = true
      stopScanner()
    }
  }, [resolveQrCode, stopScanner, t])

  return (
    <section className="qrcode">
      <div className="qrcode__scanner" aria-busy={isStarting || isResolving}>
        <video ref={videoRef} className="qrcode__video" muted playsInline />
        <div className="qrcode__frame" aria-hidden="true" />
        {isResolving && (
          <div className="qrcode__status">
            {t('qrcode.openingCertificate')}
          </div>
        )}
      </div>

      {(hasCamera === false || hasPermission === false || errorMessage) && (
        <div className="qrcode__feedback">
          {hasCamera === false && hasPermission !== false && !errorMessage && (
            <p className="qrcode__message">{t('qrcode.noCamera')}</p>
          )}
          {hasPermission === false && (
            <p className="qrcode__message">{t('qrcode.cameraPermissionDenied')}</p>
          )}
          {errorMessage && (
            <div className="qrcode__error" role="alert">
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

export default QRCode
